import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Button,
  Menu,
  MenuItem,
  Collapse,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Stack,
  Typography,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  MoreVert as MoreVertIcon,
  Add as AddIcon,
  AdminPanelSettings as AdminIcon,
  Store as StoreIcon,
  Group as GroupIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { apiClient } from '../../lib/api';
import { ReAuthPrompt } from '../../admin/components/ReAuthPrompt';
import { useAdminUsers, useAdminStores, useAdminRefetchAll } from '../../admin/context';

// Types
const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin' },
  { value: 'accounting', label: 'Accounting' },
  { value: 'insurance', label: 'Insurance' },
] as const;

type RoleValue = (typeof ROLE_OPTIONS)[number]['value'];

type AdminPermission = {
  tenantId: string;
  tenantSlug: string;
  tenantName: string;
  accessLevel: string;
  featureScope?: Array<'parts' | 'chemicals'>;
  expiresAt: string | null;
  group: { id: string; slug: string; name: string } | null;
};

type AdminUser = {
  id: string;
  email: string | null;
  status: string;
  lastSignInAt: string | null;
  permissions: AdminPermission[];
  roles: {
    tenantId: string;
    tenantSlug: string;
    tenantName: string;
    role: string;
  }[];
};

type AdminStore = {
  id: string;
  slug: string;
  name: string;
  features: Array<'parts' | 'chemicals'>;
  group: { id: string; slug: string; name: string } | null;
};

type AuditEvent = {
  id: string;
  eventType: string;
  createdAt: string;
  details: Record<string, any>;
};

type MfaFactor = {
  id: string;
  friendlyName: string;
  factorType: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  lastChallengedAt: string | null;
};

type MfaFactorsResponse = {
  userId: string;
  email: string;
  factors: MfaFactor[];
  totalFactors: number;
};

const formatDateTime = (value: string | null) => {
  if (!value) return 'Never';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

// Enhanced user row component with expansion
const UserRow: React.FC<{
  user: AdminUser;
  stores: AdminStore[];
  onAction: (userId: string, action: string, data?: any) => void;
  onAddPermission: (userId: string) => Promise<void>;
  actionLoading: string | null;
}> = ({ user, stores, onAction, onAddPermission, actionLoading }) => {
  const [expanded, setExpanded] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showAddPermission, setShowAddPermission] = useState(false);

  // Compute summary statistics
  const summary = useMemo(() => {
    const rolesByType = user.roles.reduce(
      (acc, role) => {
        acc[role.role] = (acc[role.role] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>,
    );

    const permsByType = user.permissions.reduce(
      (acc, perm) => {
        acc[perm.accessLevel] = (acc[perm.accessLevel] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>,
    );

    return { rolesByType, permsByType };
  }, [user.roles, user.permissions]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAction = (action: string) => {
    onAction(user.id, action);
    handleMenuClose();
  };

  const isLoading = (action: string) => actionLoading === `${action}-${user.id}`;

  return (
    <>
      <TableRow hover>
        {/* Email & Status */}
        <TableCell>
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {user.email ?? 'Unknown'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {user.status}
            </Typography>
          </Box>
        </TableCell>

        {/* Last Sign-In */}
        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {formatDateTime(user.lastSignInAt)}
          </Typography>
        </TableCell>

        {/* Compact Roles & Permissions Summary */}
        <TableCell>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
            {Object.entries(summary.rolesByType).map(([role, count]) => (
              <Chip
                key={role}
                icon={<AdminIcon />}
                label={`${role} (${count})`}
                size="small"
                color="primary"
                variant="outlined"
              />
            ))}
            {Object.entries(summary.permsByType).map(([accessLevel, count]) => (
              <Chip
                key={accessLevel}
                icon={<StoreIcon />}
                label={`${accessLevel} (${count})`}
                size="small"
                color="secondary"
                variant="outlined"
              />
            ))}
            {user.roles.length === 0 && user.permissions.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No access
              </Typography>
            )}
          </Stack>
        </TableCell>

        {/* Actions */}
        <TableCell align="right">
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Tooltip title={expanded ? 'Collapse' : 'Expand details'}>
              <IconButton size="small" onClick={() => setExpanded(!expanded)}>
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Add permission">
              <IconButton size="small" color="primary" onClick={() => setShowAddPermission(true)}>
                <AddIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="More actions">
              <IconButton size="small" onClick={handleMenuOpen}>
                <MoreVertIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </TableCell>
      </TableRow>

      {/* Expanded Details Row */}
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={4}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ py: 2, pl: 4 }}>
              {/* Store Access — unified view (role + permission are one record) */}
              {user.permissions.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                    Store Access ({user.permissions.length})
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                    {user.permissions.map((perm) => {
                      const role = user.roles.find((r) => r.tenantId === perm.tenantId);
                      // Show feature scope only for insurance users — admin/accounting always have full scope.
                      const isInsurance = role?.role === 'insurance';
                      const scopeLabel = isInsurance && perm.featureScope?.length
                        ? perm.featureScope
                            .map((f) => f.charAt(0).toUpperCase() + f.slice(1))
                            .join(', ')
                        : null;
                      return (
                        <Chip
                          key={perm.tenantId}
                          icon={<StoreIcon />}
                          label={
                            <>
                              <strong>{role?.role ?? 'unknown'}</strong> · {perm.tenantName}
                              {perm.group && ` (${perm.group.name})`}
                              {scopeLabel && ` · ${scopeLabel}`}
                              {perm.expiresAt &&
                                ` · Exp: ${new Date(perm.expiresAt).toLocaleDateString()}`}
                            </>
                          }
                          size="small"
                          color="primary"
                          variant="outlined"
                          onDelete={() => onAction(user.id, `revoke-permission-${perm.tenantId}`)}
                          deleteIcon={<DeleteIcon />}
                        />
                      );
                    })}
                  </Stack>
                </Box>
              )}

              {user.permissions.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No store access assigned
                </Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      {/* Actions Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={() => handleAction('view-history')}>
          View History {isLoading('audit') && <CircularProgress size={16} sx={{ ml: 1 }} />}
        </MenuItem>
        <MenuItem onClick={() => handleAction('mfa')}>
          Manage MFA {isLoading('mfa') && <CircularProgress size={16} sx={{ ml: 1 }} />}
        </MenuItem>
        {(user.status === 'invited' || user.status === 'pending') && (
          <MenuItem onClick={() => handleAction('resend')}>
            Resend Invite {isLoading('resend') && <CircularProgress size={16} sx={{ ml: 1 }} />}
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={() => handleAction('revoke-all')} sx={{ color: 'error.main' }}>
          Revoke All Access {isLoading('revoke') && <CircularProgress size={16} sx={{ ml: 1 }} />}
        </MenuItem>
      </Menu>

      {/* Add Permission Dialog */}
      <Dialog
        open={showAddPermission}
        onClose={() => setShowAddPermission(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Permission for {user.email}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Direct permission addition not yet implemented. Use the invite flow to assign stores.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddPermission(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Main component with full integration
const UserManagementRedesigned: React.FC = () => {
  const theme = useTheme();
  const { users, loading: usersLoading, error: usersError, refetch: refetchUsers } = useAdminUsers();
  const { stores, loading: storesLoading, error: storesError } = useAdminStores();
  const refetchAll = useAdminRefetchAll();
  const loading = usersLoading || storesLoading;
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [reauthPrompt, setReauthPrompt] = useState<{
    message: string;
    maxAgeMinutes?: number;
  } | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [bulkResults, setBulkResults] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'invited' | 'active' | 'pending'>('all');
  const [showAuditTrail, setShowAuditTrail] = useState(false);
  const [auditEvents, setAuditEvents] = useState<AuditEvent[]>([]);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);

  // MFA Reset state
  const [showMfaModal, setShowMfaModal] = useState(false);
  const [mfaFactors, setMfaFactors] = useState<MfaFactor[]>([]);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [showMfaResetConfirm, setShowMfaResetConfirm] = useState(false);
  const [mfaResetReason, setMfaResetReason] = useState('');

  // Invite form state
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<RoleValue | ''>('');
  const [selectedTenantIds, setSelectedTenantIds] = useState<string[]>([]);
  // Insurance-only: per-store feature scope. A store is assigned iff its array is non-empty.
  const [featureMatrix, setFeatureMatrix] = useState<Record<string, Array<'parts' | 'chemicals'>>>(
    {},
  );
  // Insurance-only: which group's stores the matrix is showing.
  const [matrixGroupId, setMatrixGroupId] = useState<string>('');

  const filteredUsers = useMemo(() => {
    const search = searchQuery.trim().toLowerCase();
    return users.filter((user) => {
      const matchesSearch = !search || user.email?.toLowerCase().includes(search);
      const normalizedStatus = user.status?.toLowerCase() ?? '';
      const matchesStatus = statusFilter === 'all' || normalizedStatus === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [users, searchQuery, statusFilter]);

  // Show context errors as global errors
  useEffect(() => {
    const ctxError = usersError || storesError;
    if (ctxError) setGlobalError(ctxError);
  }, [usersError, storesError]);

  async function inviteUser(e: React.FormEvent) {
    e.preventDefault();
    setGlobalError(null);
    setNotice(null);
    setActionLoading('invite');
    try {
      // Build the assignment list depending on role.
      let assignments: Array<{ tenantId: string; featureScope: Array<'parts' | 'chemicals'> }> = [];
      let tenantIds: string[] = [];

      if (role === 'insurance') {
        assignments = Object.entries(featureMatrix)
          .filter(([, features]) => features.length > 0)
          .map(([tenantId, featureScope]) => ({ tenantId, featureScope }));
        if (assignments.length === 0) {
          throw new Error('Select at least one store and one feature for the insurance user');
        }
        tenantIds = assignments.map((a) => a.tenantId);
      } else {
        if (!selectedTenantIds.length) {
          throw new Error('Select at least one store');
        }
        tenantIds = selectedTenantIds;
      }

      const payload: {
        email: string;
        role: RoleValue | '';
        tenantIds: string[];
        assignments?: Array<{ tenantId: string; featureScope: Array<'parts' | 'chemicals'> }>;
      } = {
        email,
        role,
        tenantIds,
      };
      // Per-store feature scope only for insurance users; backend ignores it for other roles.
      if (role === 'insurance') {
        payload.assignments = assignments;
      }
      const response = await apiClient.post('/api/admin/users/invite', payload);
      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || 'Failed to send invite');
      }
      setEmail('');
      setRole('');
      setSelectedTenantIds([]);
      setFeatureMatrix({});
      setMatrixGroupId('');
      await refetchAll();
      setNotice(`Invite sent to ${payload.email}`);
    } catch (err: any) {
      setNotice(null);
      setGlobalError(err.message || 'Failed to send invite');
    }
    setActionLoading(null);
  }

  async function handleBulkInvite() {
    if (!csvFile) return;
    setGlobalError(null);
    setNotice(null);
    setActionLoading('bulk-invite');
    try {
      const formData = new FormData();
      formData.append('file', csvFile);
      formData.append('defaultRole', role);
      if (selectedTenantIds.length === 1) {
        formData.append('tenantId', selectedTenantIds[0]);
      }

      const response = await apiClient.post('/api/admin/users/bulk-invite', formData);
      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || 'Failed to upload CSV');
      }
      const data = await response.json();
      setBulkResults(data);
      setNotice(`Invited ${data.succeeded ?? 0} users. ${data.failed ?? 0} failed.`);
      setCsvFile(null);
      await refetchAll();
    } catch (err: any) {
      setBulkResults(null);
      setNotice(null);
      setGlobalError(err.message || 'Bulk invite failed');
    }
    setActionLoading(null);
  }

  async function handleResend(userId: string) {
    setGlobalError(null);
    setNotice(null);
    setActionLoading(`resend-${userId}`);
    try {
      const response = await apiClient.post(`/api/admin/users/${userId}/resend`, {});
      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || 'Failed to resend invite');
      }
      const user = users.find((u) => u.id === userId);
      setNotice(`Invite resent to ${user?.email ?? 'user'}`);
    } catch (err: any) {
      setNotice(null);
      setGlobalError(err.message || 'Failed to resend invite');
    }
    setActionLoading(null);
  }

  async function handleRevokeAll(userId: string) {
    const user = users.find((u) => u.id === userId);
    if (!confirm(`Revoke all access for ${user?.email ?? 'this user'}? This cannot be undone.`)) {
      return;
    }

    const storeSlugs =
      user?.permissions
        .filter((permission) => permission.tenantSlug)
        .map((permission) => permission.tenantSlug) || [];

    if (!storeSlugs.length) {
      setGlobalError('User has no assignable permissions to revoke.');
      return;
    }

    setGlobalError(null);
    setNotice(null);
    setActionLoading(`revoke-${userId}`);
    try {
      const response = await apiClient.post(`/api/admin/users/${userId}/revoke`, {
        stores: storeSlugs,
      });
      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || 'Failed to revoke access');
      }
      await refetchAll();
      setNotice(`Access revoked for ${user?.email ?? 'user'}`);
    } catch (err: any) {
      setNotice(null);
      setGlobalError(err.message || 'Failed to revoke access');
    }
    setActionLoading(null);
  }

  async function loadAuditTrail(userId: string) {
    const user = users.find((u) => u.id === userId);
    setGlobalError(null);
    setNotice(null);
    setActionLoading(`audit-${userId}`);
    try {
      const response = await apiClient.get(`/api/admin/users/${userId}/audit-trail?limit=50`);
      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || 'Failed to load audit trail');
      }
      const data = await response.json();
      setAuditEvents(Array.isArray(data.events) ? data.events : []);
      setSelectedUser(user || null);
      setShowAuditTrail(true);
    } catch (err: any) {
      setGlobalError(err.message || 'Failed to load audit trail');
    }
    setActionLoading(null);
  }

  const closeAuditTrail = () => {
    setShowAuditTrail(false);
    setAuditEvents([]);
    setSelectedUser(null);
  };

  const loadMfaFactors = async (userId: string) => {
    const user = users.find((u) => u.id === userId);
    setSelectedUser(user || null);
    setMfaLoading(true);
    setGlobalError(null);
    setActionLoading(`mfa-${userId}`);

    try {
      const res = await apiClient.get(`/api/admin/users/${userId}/mfa-factors`);
      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Failed to load MFA factors (${res.status}): ${errorText}`);
      }
      const data: MfaFactorsResponse = await res.json();
      setMfaFactors(data.factors);
      setShowMfaModal(true);
    } catch (err: any) {
      setNotice(null);
      setGlobalError(err.message || 'Failed to load MFA factors');
    } finally {
      setMfaLoading(false);
      setActionLoading(null);
    }
  };

  const closeMfaModal = () => {
    setShowMfaModal(false);
    setMfaFactors([]);
    setSelectedUser(null);
  };

  const promptMfaReset = () => {
    setShowMfaResetConfirm(true);
  };

  const cancelMfaReset = () => {
    setShowMfaResetConfirm(false);
    setMfaResetReason('');
  };

  const confirmMfaReset = async () => {
    if (!selectedUser) return;

    setMfaLoading(true);
    setGlobalError(null);
    setActionLoading(`mfa-reset-${selectedUser.id}`);
    setReauthPrompt(null);

    try {
      const res = await apiClient.post(`/api/admin/users/${selectedUser.id}/reset-mfa`, {
        reason: mfaResetReason || 'Admin reset via UI',
      });

      if (!res.ok) {
        const errorText = await res.text();
        let errorPayload: any = null;
        try {
          errorPayload = JSON.parse(errorText);
        } catch {
          // Failed to parse error response, will use errorText instead
        }

        if (res.status === 403 && errorPayload?.code === 'RECENT_AUTH_REQUIRED') {
          setNotice(null);
          setReauthPrompt({
            message: errorPayload.message || 'Recent authentication required',
            maxAgeMinutes: errorPayload.maxAgeMinutes,
          });
          setGlobalError(errorPayload.message || 'Recent authentication required');
          return;
        }

        if (res.status === 429) {
          setNotice(null);
          setGlobalError('Rate limit exceeded. Please wait before retrying this action.');
          return;
        }

        throw new Error(
          `Failed to reset MFA (${res.status}): ${errorPayload?.message || errorText || 'Unknown error'}`,
        );
      }

      const result = await res.json();
      setNotice(result.message || `Successfully removed ${result.factorsRemoved} MFA factor(s)`);
      setGlobalError(null);

      // Reload MFA factors to show empty state
      await loadMfaFactors(selectedUser.id);

      // Close confirmation dialog
      setShowMfaResetConfirm(false);
      setMfaResetReason('');
    } catch (err: any) {
      setNotice(null);
      setGlobalError(err.message || 'Failed to reset MFA');
    } finally {
      setMfaLoading(false);
      setActionLoading(null);
    }
  };

  const handleAction = async (userId: string, action: string) => {
    if (action === 'view-history') {
      await loadAuditTrail(userId);
    } else if (action === 'mfa') {
      await loadMfaFactors(userId);
    } else if (action === 'resend') {
      await handleResend(userId);
    } else if (action === 'revoke-all') {
      await handleRevokeAll(userId);
    } else if (action.startsWith('revoke-role-') || action.startsWith('revoke-permission-')) {
      // Handle individual revocation
      const user = users.find((u) => u.id === userId);
      if (!user) return;

      const tenantId = action.replace('revoke-role-', '').replace('revoke-permission-', '');
      const isRole = action.startsWith('revoke-role-');

      if (isRole) {
        // Revoke role - not implemented in current API, would need endpoint
        setGlobalError('Individual role revocation not yet implemented');
        return;
      }

      // Revoke permission
      const permission = user.permissions.find((p) => p.tenantId === tenantId);
      if (!permission) return;

      const revokeStores = permission.tenantSlug ? [permission.tenantSlug] : [];

      setActionLoading(`revoke-${userId}`);
      try {
        const response = await apiClient.post(`/api/admin/users/${userId}/revoke`, {
          stores: revokeStores,
        });
        if (!response.ok) {
          const message = await response.text();
          throw new Error(message || 'Failed to revoke access');
        }
        await refetchAll();
        setNotice(`Permission revoked for ${user.email ?? 'user'}`);
      } catch (err: any) {
        setGlobalError(err.message || 'Failed to revoke permission');
      }
      setActionLoading(null);
    }
  };

  const handleAddPermission = async (userId: string) => {
    setActionLoading(`add-permission-${userId}`);
    try {
      // This would need a dedicated API endpoint - using invite as template
      setGlobalError('Direct permission addition not yet implemented. Use invite flow.');
      // TODO: Implement POST /api/admin/users/{userId}/permissions endpoint
    } catch (err: any) {
      setGlobalError(err.message || 'Failed to add permission');
    }
    setActionLoading(null);
  };

  return (
    <Box sx={{ p: 3, bgcolor: 'background.default', minHeight: '100vh' }}>
      <Typography variant="h4" gutterBottom color="text.primary">
        User Management
      </Typography>

      {/* Notifications */}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}
      {globalError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setGlobalError(null)}>
          {globalError}
        </Alert>
      )}

      {/* Invite Form */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.paper' }}>
        <Typography variant="h6" gutterBottom color="text.primary">
          Invite Team Member
        </Typography>
        <Box component="form" onSubmit={inviteUser} sx={{ display: 'grid', gap: 2, maxWidth: 600 }}>
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            fullWidth
          />
          <FormControl fullWidth>
            {role && <InputLabel>Role</InputLabel>}
            <Select
              value={role}
              label={role ? 'Role' : ''}
              onChange={(e) => setRole(e.target.value as RoleValue)}
              displayEmpty
            >
              <MenuItem value="" disabled>Select a role</MenuItem>
              {ROLE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {(
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                {role === 'insurance' ? 'Store & Feature Access' : 'Select Stores'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {role === 'insurance'
                  ? 'Pick a group, then choose which reports this insurance user can access per store. A store is assigned only if at least one feature is checked.'
                  : 'Pick a group, then select the stores this user can access.'}
              </Typography>
              {(() => {
                const isInsurance = role === 'insurance';
                const groupMap = new Map<string, { id: string; name: string }>();
                stores.forEach((s) => {
                  const gid = s.group?.id ?? 'ungrouped';
                  const gname = s.group?.name ?? 'Ungrouped';
                  if (!groupMap.has(gid)) groupMap.set(gid, { id: gid, name: gname });
                });
                const groupOptions = Array.from(groupMap.values());
                const activeGroupId = matrixGroupId || groupOptions[0]?.id || '';
                const groupStores = stores.filter(
                  (s) => (s.group?.id ?? 'ungrouped') === activeGroupId,
                );

                const allFeatures: Array<'parts' | 'chemicals'> = ['parts', 'chemicals'];

                const supporting = (feature: 'parts' | 'chemicals') =>
                  groupStores.filter((s) => (s.features ?? []).includes(feature));
                const colAllChecked = (feature: 'parts' | 'chemicals') => {
                  const elig = supporting(feature);
                  return (
                    elig.length > 0 &&
                    elig.every((s) => (featureMatrix[s.id] ?? []).includes(feature))
                  );
                };
                const colSomeChecked = (feature: 'parts' | 'chemicals') =>
                  supporting(feature).some((s) => (featureMatrix[s.id] ?? []).includes(feature));

                const toggleCell = (storeId: string, feature: 'parts' | 'chemicals') => {
                  setFeatureMatrix((prev) => {
                    const current = prev[storeId] ?? [];
                    const next = current.includes(feature)
                      ? current.filter((f) => f !== feature)
                      : [...current, feature];
                    return { ...prev, [storeId]: next };
                  });
                };

                const toggleColumn = (feature: 'parts' | 'chemicals', checked: boolean) => {
                  setFeatureMatrix((prev) => {
                    const next = { ...prev };
                    supporting(feature).forEach((s) => {
                      const current = next[s.id] ?? [];
                      if (checked && !current.includes(feature)) {
                        next[s.id] = [...current, feature];
                      } else if (!checked) {
                        next[s.id] = current.filter((f) => f !== feature);
                      }
                    });
                    return next;
                  });
                };

                return (
                  <>
                    <FormControl fullWidth sx={{ mb: 1.5, maxWidth: 360 }}>
                      <InputLabel>Group</InputLabel>
                      <Select
                        value={activeGroupId}
                        label="Group"
                        onChange={(e) => {
                          setMatrixGroupId(e.target.value);
                          setFeatureMatrix({});
                          setSelectedTenantIds([]);
                        }}
                      >
                        {groupOptions.map((g) => (
                          <MenuItem key={g.id} value={g.id}>
                            {g.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <TableContainer
                      component={Paper}
                      variant="outlined"
                      sx={{ maxHeight: 320, overflow: 'auto' }}
                    >
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Store</TableCell>
                            {isInsurance ? (
                              allFeatures.map((feature) => (
                                <TableCell key={feature} align="center">
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      sx={{ textTransform: 'capitalize' }}
                                    >
                                      {feature}
                                    </Typography>
                                    <Checkbox
                                      size="small"
                                      checked={colAllChecked(feature)}
                                      indeterminate={
                                        !colAllChecked(feature) && colSomeChecked(feature)
                                      }
                                      disabled={supporting(feature).length === 0}
                                      onChange={(e) => toggleColumn(feature, e.target.checked)}
                                    />
                                  </Box>
                                </TableCell>
                              ))
                            ) : (
                              <TableCell align="center">
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                  }}
                                >
                                  <Typography variant="caption">Select</Typography>
                                  <Checkbox
                                    size="small"
                                    checked={
                                      groupStores.length > 0 &&
                                      groupStores.every((s) => selectedTenantIds.includes(s.id))
                                    }
                                    indeterminate={
                                      groupStores.some((s) => selectedTenantIds.includes(s.id)) &&
                                      !groupStores.every((s) => selectedTenantIds.includes(s.id))
                                    }
                                    onChange={(e) => {
                                      const ids = groupStores.map((s) => s.id);
                                      if (e.target.checked) {
                                        setSelectedTenantIds((prev) => [
                                          ...new Set([...prev, ...ids]),
                                        ]);
                                      } else {
                                        setSelectedTenantIds((prev) =>
                                          prev.filter((id) => !ids.includes(id)),
                                        );
                                      }
                                    }}
                                  />
                                </Box>
                              </TableCell>
                            )}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {groupStores.map((store) => (
                            <TableRow key={store.id} hover>
                              <TableCell>{store.name}</TableCell>
                              {isInsurance ? (
                                allFeatures.map((feature) => {
                                  const supported = (store.features ?? []).includes(feature);
                                  const checked = (featureMatrix[store.id] ?? []).includes(feature);
                                  return (
                                    <TableCell key={feature} align="center">
                                      <Tooltip
                                        title={supported ? '' : 'Not enabled on this store'}
                                        disableHoverListener={supported}
                                      >
                                        <span>
                                          <Checkbox
                                            size="small"
                                            checked={checked}
                                            disabled={!supported}
                                            onChange={() => toggleCell(store.id, feature)}
                                          />
                                        </span>
                                      </Tooltip>
                                    </TableCell>
                                  );
                                })
                              ) : (
                                <TableCell align="center">
                                  <Checkbox
                                    size="small"
                                    checked={selectedTenantIds.includes(store.id)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedTenantIds((prev) => [...prev, store.id]);
                                      } else {
                                        setSelectedTenantIds((prev) =>
                                          prev.filter((id) => id !== store.id),
                                        );
                                      }
                                    }}
                                  />
                                </TableCell>
                              )}
                            </TableRow>
                          ))}
                          {groupStores.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={isInsurance ? allFeatures.length + 1 : 2}>
                                <Typography variant="caption" color="text.secondary">
                                  No stores in this group.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                );
              })()}
            </Box>
          )}
          <Button
            type="submit"
            variant="contained"
            disabled={
              loading ||
              actionLoading === 'invite' ||
              !role ||
              (role === 'insurance'
                ? !Object.values(featureMatrix).some((f) => f.length > 0)
                : !selectedTenantIds.length)
            }
            sx={{ maxWidth: 200 }}
          >
            {actionLoading === 'invite' ? 'Sending...' : 'Send Invite'}
          </Button>
        </Box>
      </Paper>

      {/* Bulk Invite */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.paper' }}>
        <Typography variant="h6" gutterBottom color="text.primary">
          Bulk Invite
        </Typography>
        <Stack spacing={2} sx={{ maxWidth: 600 }}>
          <input
            type="file"
            accept=".csv"
            onChange={(e) => {
              setCsvFile(e.target.files?.[0] || null);
              setBulkResults(null);
            }}
          />
          <Button
            variant="contained"
            onClick={handleBulkInvite}
            disabled={!csvFile || actionLoading === 'bulk-invite'}
            sx={{ maxWidth: 200 }}
          >
            {actionLoading === 'bulk-invite' ? 'Uploading...' : 'Upload CSV'}
          </Button>
          {bulkResults && (
            <Alert severity={bulkResults.failed > 0 ? 'warning' : 'success'}>
              <Typography variant="body2">Success: {bulkResults.succeeded ?? 0}</Typography>
              <Typography variant="body2">Failed: {bulkResults.failed ?? 0}</Typography>
              {Array.isArray(bulkResults.failures) && bulkResults.failures.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  {bulkResults.failures.map((failure: any) => (
                    <Typography
                      key={`failure-${failure.row}-${failure.email}`}
                      variant="caption"
                      display="block"
                    >
                      Row {failure.row}: {failure.email} — {failure.error}
                    </Typography>
                  ))}
                </Box>
              )}
            </Alert>
          )}
        </Stack>
      </Paper>

      {/* Users Table */}
      <Paper sx={{ p: 2, bgcolor: 'background.paper' }}>
        <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }} color="text.primary">
            Users
          </Typography>
          <TextField
            size="small"
            placeholder="Search by email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ minWidth: 200 }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="invited">Invited</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow
                sx={{
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'grey.50',
                }}
              >
                <TableCell>
                  <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                    User
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                    Last Sign-In
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                    Access Summary
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                    Actions
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <CircularProgress size={24} sx={{ my: 2 }} />
                  </TableCell>
                </TableRow>
              ) : filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography color="text.secondary" sx={{ py: 2 }}>
                      No users found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => (
                  <UserRow
                    key={user.id}
                    user={user}
                    stores={stores}
                    onAction={handleAction}
                    onAddPermission={handleAddPermission}
                    actionLoading={actionLoading}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Audit Trail Modal */}
      {showAuditTrail && (
        <Dialog open={showAuditTrail} onClose={closeAuditTrail} maxWidth="md" fullWidth>
          <DialogTitle>Audit Trail: {selectedUser?.email ?? 'User'}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              {auditEvents.length ? (
                auditEvents.map((event) => (
                  <Paper key={event.id} variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {formatDateTime(event.createdAt)}
                      </Typography>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {event.eventType}
                      </Typography>
                    </Box>
                    <Box
                      component="pre"
                      sx={{
                        m: 0,
                        p: 1,
                        bgcolor:
                          theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'grey.100',
                        color: 'text.primary',
                        borderRadius: 1,
                        fontSize: 12,
                        overflowX: 'auto',
                      }}
                    >
                      {JSON.stringify(event.details ?? {}, null, 2)}
                    </Box>
                  </Paper>
                ))
              ) : (
                <Typography color="text.secondary">No audit events found for this user.</Typography>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeAuditTrail}>Close</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* MFA Factors Modal */}
      {showMfaModal && (
        <Dialog open={showMfaModal} onClose={closeMfaModal} maxWidth="sm" fullWidth>
          <DialogTitle>MFA Status: {selectedUser?.email ?? 'User'}</DialogTitle>
          <DialogContent>
            {mfaLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress />
              </Box>
            ) : mfaFactors.length > 0 ? (
              <>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                  This user has {mfaFactors.length} MFA factor(s) enrolled:
                </Typography>
                <Stack spacing={2} sx={{ mb: 2 }}>
                  {mfaFactors.map((factor) => (
                    <Paper key={factor.id} variant="outlined" sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography fontWeight={600}>
                          {factor.friendlyName || 'Authenticator'}
                        </Typography>
                        <Chip
                          label={factor.status}
                          size="small"
                          color={factor.status === 'verified' ? 'success' : 'warning'}
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Type: {factor.factorType.toUpperCase()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Created: {formatDateTime(factor.createdAt)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Last Used:{' '}
                        {factor.lastChallengedAt
                          ? formatDateTime(factor.lastChallengedAt)
                          : 'Never'}
                      </Typography>
                    </Paper>
                  ))}
                </Stack>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    Resetting MFA will remove all authenticator factors for this user. They will
                    need to log in with their password and re-enroll in MFA if required.
                  </Typography>
                </Alert>
                <Button
                  variant="contained"
                  color="error"
                  fullWidth
                  onClick={promptMfaReset}
                  disabled={mfaLoading}
                >
                  Reset MFA for this User
                </Button>
              </>
            ) : (
              <Alert severity="success">
                <Typography variant="body2">
                  This user does not have any MFA factors enrolled. They can log in with their
                  password only.
                </Typography>
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeMfaModal}>Close</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* MFA Reset Confirm Dialog */}
      {showMfaResetConfirm && (
        <Dialog open={showMfaResetConfirm} onClose={cancelMfaReset} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ color: 'error.main' }}>Confirm MFA Reset</DialogTitle>
          <DialogContent>
            <Typography gutterBottom>
              Are you sure you want to reset MFA for <strong>{selectedUser?.email}</strong>?
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              This will permanently delete all MFA factors for this user. They will need to
              re-enroll if MFA is required.
            </Typography>
            <TextField
              label="Reason for reset (optional but recommended)"
              multiline
              rows={3}
              fullWidth
              value={mfaResetReason}
              onChange={(e) => setMfaResetReason(e.target.value)}
              placeholder="e.g., User lost phone with authenticator"
              helperText="This will be recorded in the audit log"
              disabled={mfaLoading}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={cancelMfaReset} disabled={mfaLoading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={confirmMfaReset}
              disabled={mfaLoading}
            >
              {mfaLoading ? 'Resetting...' : 'Reset MFA'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* ReAuth Prompt */}
      {reauthPrompt && (
        <ReAuthPrompt
          open
          message={reauthPrompt.message}
          maxAgeMinutes={reauthPrompt.maxAgeMinutes}
          onClose={() => setReauthPrompt(null)}
        />
      )}
    </Box>
  );
};

export default UserManagementRedesigned;
